import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, Chip, DialogActions, DialogContent, DialogTitle, MenuItem, Pagination,
    Snackbar, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import Dialog from '../components/ActionDialog';
import api from '../services/Api';
import { panelSx } from '../theme';
import useAdminPasswordConfirmation, { adminPasswordConfig, adminPasswordErrorKey } from '../hooks/useAdminPasswordConfirmation';

const PAGE_SIZE = 20;
const pad = value => String(value).padStart(2, '0');
const toLocalInput = value => {
    if (!value) return '';
    const date = new Date(value);
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};
const formatLocalDateTime = value => {
    if (!value) return '—';
    const date = new Date(value);
    return `${date.getFullYear()}/${pad(date.getMonth() + 1)}/${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};
const empty = () => ({ id: crypto.randomUUID(), type: 'Maintenance', status: 'Draft', message: '', announcementStartAt: '', startAt: '', endAt: '', isNew: true });

export default function AdminAnnouncements() {
    const { t } = useTranslation();
    const { requestPassword, passwordDialog } = useAdminPasswordConfirmation();
    const [result, setResult] = useState({ items: [], total: 0 });
    const [filters, setFilters] = useState({ status: '', type: '' });
    const [appliedFilters, setAppliedFilters] = useState({ status: '', type: '' });
    const [page, setPage] = useState(1);
    const [edit, setEdit] = useState(null);
    const [busy, setBusy] = useState(false);
    const [notice, setNotice] = useState(null);
    const load = useCallback(async () => {
        try {
            const response = await api.get('/api/announcements/admin', {
                params: { page, pageSize: PAGE_SIZE, status: appliedFilters.status || undefined, type: appliedFilters.type || undefined },
            });
            const data = response.data;
            setResult(Array.isArray(data) ? { items: data, total: data.length } : data);
        } catch (_) { setNotice({ severity: 'error', text: t('announcement.loadFailed') }); }
    }, [appliedFilters.status, appliedFilters.type, page, t]);
    useEffect(() => { void load(); }, [load]);
    const applyFilters = event => {
        event.preventDefault();
        setPage(1);
        setAppliedFilters(filters);
    };
    const select = item => setEdit({
        ...item,
        announcementStartAt: toLocalInput(item.announcementStartAtUtc),
        startAt: toLocalInput(item.startAtUtc),
        endAt: toLocalInput(item.endAtUtc),
        isNew: false,
    });
    const save = async event => {
        event.preventDefault();
        if (busy || !edit) return;
        if (!edit.announcementStartAt) {
            setNotice({ severity: 'error', text: t('announcement.announceStartRequired') }); return;
        }
        if (edit.type === 'Maintenance' && (!edit.startAt || !edit.endAt || new Date(edit.startAt) >= new Date(edit.endAt)
            || new Date(edit.announcementStartAt) >= new Date(edit.endAt))) {
            setNotice({ severity: 'error', text: t('announcement.invalidDates') }); return;
        }
        if (edit.type === 'Manual' && !edit.message.trim()) {
            setNotice({ severity: 'error', text: t('announcement.messageRequired') }); return;
        }
        const password = await requestPassword();
        if (password === null) return;
        setBusy(true); setNotice(null);
        try {
            await api.put(`/api/announcements/admin/${edit.id}`, {
                type: edit.type, status: edit.status, message: edit.type === 'Manual' ? edit.message.trim() : null,
                // datetime-local represents the administrator's local time;
                // ISO preserves that exact instant for every player's timezone.
                announcementStartAt: new Date(edit.announcementStartAt).toISOString(),
                startAt: edit.type === 'Maintenance' ? new Date(edit.startAt).toISOString() : null,
                endAt: edit.type === 'Maintenance' ? new Date(edit.endAt).toISOString() : null,
            }, adminPasswordConfig(password));
            await load(); setEdit(null); setNotice({ severity: 'success', text: t('announcement.saved') });
        } catch (error) {
            const key = adminPasswordErrorKey(error) || (error?.response?.data?.code === 'PENDING_ANNOUNCEMENT_EXISTS'
                ? 'announcement.pendingExists' : 'announcement.saveFailed');
            setNotice({ severity: 'error', text: t(key) });
        } finally { setBusy(false); }
    };
    const pageCount = Math.max(1, Math.ceil(result.total / PAGE_SIZE));
    return <main className="app-page dashboard-page">
        <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                <Typography component="h1" variant="h5">{t('announcement.manage')}</Typography>
                <Button type="button" variant="contained" onClick={() => setEdit(empty())}>{t('announcement.new')}</Button>
            </Box>
            <Box sx={panelSx}>
                <Typography component="h2" variant="h6" sx={{ mb: 1 }}>{t('announcement.history')}</Typography>
                <Box component="form" onSubmit={applyFilters} sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <TextField select size="small" label={t('announcement.status')} value={filters.status}
                        onChange={event => setFilters(current => ({ ...current, status: event.target.value }))} sx={{ minWidth: 170 }}>
                        <MenuItem value="">{t('announcement.allStatuses')}</MenuItem>
                        {['Draft', 'Scheduled', 'Pending', 'Finished', 'Cancelled'].map(value => <MenuItem key={value} value={value}>{t(`announcement.${value}`)}</MenuItem>)}
                    </TextField>
                    <TextField select size="small" label={t('announcement.type')} value={filters.type}
                        onChange={event => setFilters(current => ({ ...current, type: event.target.value }))} sx={{ minWidth: 190 }}>
                        <MenuItem value="">{t('announcement.allTypes')}</MenuItem>
                        {['Maintenance', 'Manual'].map(value => <MenuItem key={value} value={value}>{t(`announcement.${value}`)}</MenuItem>)}
                    </TextField>
                    <Button type="submit" variant="outlined">{t('announcement.applyFilters')}</Button>
                </Box>
                <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflowX: 'auto' }}>
                    <Table size="small" sx={{ minWidth: 760 }}>
                        <TableHead><TableRow>
                            <TableCell>{t('announcement.type')}</TableCell>
                            <TableCell>{t('announcement.status')}</TableCell>
                            <TableCell>{t('announcement.announceFrom')}</TableCell>
                            <TableCell>{t('announcement.details')}</TableCell>
                            <TableCell align="right">{t('announcement.actions')}</TableCell>
                        </TableRow></TableHead>
                        <TableBody>
                            {result.items.map(item => <TableRow key={item.id} hover>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{t(`announcement.${item.type}`)}</TableCell>
                                <TableCell><Chip size="small" label={t(`announcement.${item.status}`)}
                                    color={item.status === 'Pending' ? 'warning' : item.status === 'Scheduled' ? 'info' : 'default'} /></TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatLocalDateTime(item.announcementStartAtUtc)}</TableCell>
                                <TableCell sx={{ minWidth: 260, maxWidth: 460, whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
                                    {item.type === 'Manual' ? item.message : `${formatLocalDateTime(item.startAtUtc)} – ${formatLocalDateTime(item.endAtUtc)}`}
                                </TableCell>
                                <TableCell align="right"><Button type="button" variant="outlined" size="small" onClick={() => select(item)}>{t('subscription.edit')}</Button></TableCell>
                            </TableRow>)}
                            {result.items.length === 0 && <TableRow><TableCell colSpan={5} align="center">{t('announcement.empty')}</TableCell></TableRow>}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Pagination count={pageCount} page={page} showFirstButton showLastButton
                    onChange={(_, value) => setPage(value)}
                    sx={{ mt: 2, display: 'flex', justifyContent: 'center' }} />
            </Box>
            <Dialog open={!!edit} onClose={() => setEdit(null)} fullWidth maxWidth="sm"
                PaperProps={{ component: 'form', onSubmit: save }}>
                <DialogTitle>{t(edit?.isNew ? 'announcement.newTitle' : 'announcement.editTitle')}</DialogTitle>
                <DialogContent>
                    <Alert severity={edit?.isNew ? 'info' : 'warning'} sx={{ mb: 2 }}>
                        {t(edit?.isNew ? 'announcement.newDescription' : 'announcement.editDescription')}
                    </Alert>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, pt: 1 }}>
                        <TextField select label={t('announcement.type')} value={edit?.type || ''} disabled={busy}
                            onChange={event => setEdit(current => ({ ...current, type: event.target.value }))}>
                            {['Maintenance', 'Manual'].map(value => <MenuItem key={value} value={value}>{t(`announcement.${value}`)}</MenuItem>)}
                        </TextField>
                        <TextField select label={t('announcement.status')} value={edit?.status || ''} disabled={busy}
                            onChange={event => setEdit(current => ({ ...current, status: event.target.value }))}>
                            {['Draft', 'Scheduled', 'Pending', 'Finished', 'Cancelled'].map(value => <MenuItem key={value} value={value}>{t(`announcement.${value}`)}</MenuItem>)}
                        </TextField>
                        <TextField required type="datetime-local" InputLabelProps={{ shrink: true }} label={t('announcement.announceFrom')}
                            value={edit?.announcementStartAt || ''} disabled={busy}
                            onChange={event => setEdit(current => ({ ...current, announcementStartAt: event.target.value }))}
                            sx={{ gridColumn: '1 / -1' }} />
                        {edit?.type === 'Maintenance' ? <>
                            <TextField required type="datetime-local" InputLabelProps={{ shrink: true }} label={t('announcement.start')}
                                value={edit.startAt} disabled={busy} onChange={event => setEdit(current => ({ ...current, startAt: event.target.value }))} />
                            <TextField required type="datetime-local" InputLabelProps={{ shrink: true }} label={t('announcement.end')}
                                value={edit.endAt} disabled={busy} onChange={event => setEdit(current => ({ ...current, endAt: event.target.value }))} />
                        </> : <TextField required multiline minRows={3} inputProps={{ maxLength: 1000 }} label={t('announcement.message')}
                            value={edit?.message || ''} disabled={busy} onChange={event => setEdit(current => ({ ...current, message: event.target.value }))}
                            sx={{ gridColumn: '1 / -1' }} />}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        {t('announcement.timezone')}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button type="button" disabled={busy} onClick={() => setEdit(null)}>{t('common.cancel')}</Button>
                    <Button type="submit" variant="contained" disabled={busy}>{t('announcement.save')}</Button>
                </DialogActions>
            </Dialog>
            <Snackbar open={!!notice} autoHideDuration={notice?.severity === 'success' ? 3500 : 6500}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} onClose={() => setNotice(null)}>
                <Alert severity={notice?.severity || 'info'} variant="filled" onClose={() => setNotice(null)}>{notice?.text}</Alert>
            </Snackbar>
            {passwordDialog}
        </Box>
    </main>;
}
